import {useEffect, useRef} from 'react'
import {useLocation, useHistory} from 'react-router-dom'
import {isPreviewing} from '@builder.io/sdk-react'
import {getSegmentReady} from '~/app/utils/segment/config'
import {getDeviceType} from '../../helpers'
import {useCustomerType} from '@salesforce/commerce-sdk-react'
import useLearningCenter from '~/app/hooks/use-learning-center'
import {useIsLearningCenter} from 'hec/hooks/learning-center/useIsLearningCenter'

export const LearningCenterNavTracker = () => {
    const location = useLocation()
    const history = useHistory()
    const {isRegistered} = useCustomerType()
    const {getActiveGoal} = useLearningCenter()
    const activeGoal = getActiveGoal()
    const isLearningCenter = useIsLearningCenter()

    const prevPathRef = useRef(location.pathname)
    const hasFiredEnterEvent = useRef(false)

    const wasLearningCenter = prevPathRef.current.split('/').includes('learning-center')

    const isPreview = isPreviewing()

    useEffect(() => {
        if (isPreview) return

        const trackLearningCenterExit = async () => {
            if (!isRegistered || (isRegistered && !activeGoal)) {
                const analytics = await getSegmentReady()
                if (!analytics) return
                const urlPath = location.pathname
                analytics.track('Exited Learning Center', {
                    exitPageUrl: `${window.location.origin}${urlPath}`,
                    exitTimeStamp: new Date().toISOString(),
                    device: getDeviceType()
                })
            }
        }

        const trackLearningCenterEnter = async (newPathname) => {
            const analytics = await getSegmentReady()
            if (!analytics) return
            analytics.track('Entered Learning Center', {
                entryPageUrl: `${window.location.origin}${newPathname}`,
                entryTimeStamp: new Date().toISOString(),
                device: getDeviceType()
            })
        }

        if (isLearningCenter && !hasFiredEnterEvent.current) {
            hasFiredEnterEvent.current = true
            trackLearningCenterEnter(location.pathname)
        }

        const unlisten = history.listen((newLocation) => {
            const isNewLearningCenter = newLocation.pathname.split('/').includes('learning-center')
            const isEnteringLearningCenter = isNewLearningCenter && !wasLearningCenter
            const isExitingLearningCenter = !isNewLearningCenter && wasLearningCenter

            if (isExitingLearningCenter) {
                hasFiredEnterEvent.current = false
                trackLearningCenterExit()
            }

            if (isEnteringLearningCenter && !hasFiredEnterEvent.current) {
                hasFiredEnterEvent.current = true
                trackLearningCenterEnter(newLocation.pathname)
            }

            prevPathRef.current = newLocation.pathname
        })

        const handleBeforeUnload = () => {
            if (isLearningCenter) {
                trackLearningCenterExit()
            }
        }
        window.addEventListener('beforeunload', handleBeforeUnload)

        return () => {
            unlisten()
            window.removeEventListener('beforeunload', handleBeforeUnload)
        }
    }, [
        history,
        wasLearningCenter,
        isPreview,
        location.pathname,
        activeGoal,
        isRegistered,
        isLearningCenter
    ])

    return null
}
